import React, { useState, useEffect, useCallback } from 'react';
import { Box, Button, Container, Typography, Stack, Paper, CircularProgress, List, ListItem, ListItemText, Divider, Avatar, Alert } from '@mui/material';
import { PublicClientApplication } from '@azure/msal-browser';
import Iconify from '../components/Iconify';
import Page from '../components/Page';
import axios from 'axios';

// Set in the client's .env as REACT_APP_OUTLOOK_CLIENT_ID (and optionally
// REACT_APP_OUTLOOK_TENANT_ID) — see the Azure app registration notes below.
const CLIENT_ID = process.env.REACT_APP_OUTLOOK_CLIENT_ID || '';
const TENANT_ID = process.env.REACT_APP_OUTLOOK_TENANT_ID || 'common';
const REDIRECT_URI = window.location.origin + '/email';
const SCOPES = ['User.Read', 'Mail.Read'];

// Single MSAL instance per browser tab; each signed-in Microsoft account is
// tracked separately inside it, so this being module-level is safe.
let msalInstance = null;
let msalReady = null;

function getMsal() {
    if (!CLIENT_ID) return null;
    if (!msalInstance) {
        msalInstance = new PublicClientApplication({
            auth: {
                clientId: CLIENT_ID,
                authority: `https://login.microsoftonline.com/${TENANT_ID}`,
                redirectUri: REDIRECT_URI,
            },
            cache: {
                cacheLocation: 'localStorage',
            },
        });
        msalReady = msalInstance.initialize();
    }
    return msalInstance;
}

export default function EmailPage() {
    const [ipmsUser, setIpmsUser] = useState(null);
    const [userLoading, setUserLoading] = useState(true);
    // The Microsoft account THIS signed-in IPMS user has personally linked —
    // never shared with or inferred from any other IPMS user on this browser.
    const [account, setAccount] = useState(null);
    const [emails, setEmails] = useState([]);
    const [loading, setLoading] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        // Fetch the currently logged-in IPMS user to scope the Outlook connection to them
        axios.get('https://demo.insollex.com/api/user', { withCredentials: true })
            .then(res => setIpmsUser(res.data))
            .catch(err => console.error('Failed to fetch user', err))
            .finally(() => setUserLoading(false));
    }, []);

    // Restore this IPMS user's own linked Microsoft account, if MSAL still has it cached
    useEffect(() => {
        if (!ipmsUser || !CLIENT_ID) return;
        const msal = getMsal();
        if (!msal) return;

        let cancelled = false;
        (async () => {
            await msalReady;
            const linkedHomeAccountId = localStorage.getItem(`outlook_account_${ipmsUser.id}`);
            if (!linkedHomeAccountId) return;

            const existingAccount = msal.getAccountByHomeId(linkedHomeAccountId);
            if (cancelled) return;
            if (existingAccount) {
                setAccount(existingAccount);
            } else {
                // Cached Microsoft session is gone (cleared cache, different browser, etc.)
                localStorage.removeItem(`outlook_account_${ipmsUser.id}`);
            }
        })();

        return () => { cancelled = true; };
    }, [ipmsUser]);

    const acquireToken = useCallback(async (interactive) => {
        const msal = getMsal();
        await msalReady;

        if (account) {
            try {
                const result = await msal.acquireTokenSilent({ scopes: SCOPES, account });
                return result.accessToken;
            } catch (silentError) {
                if (!interactive) throw silentError;
            }
        }

        const result = await msal.loginPopup({ scopes: SCOPES });
        localStorage.setItem(`outlook_account_${ipmsUser.id}`, result.account.homeAccountId);
        setAccount(result.account);
        return result.accessToken;
    }, [account, ipmsUser]);

    const fetchEmails = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const token = await acquireToken(false);
            const response = await axios.get('https://graph.microsoft.com/v1.0/me/messages', {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                    $top: 20, // Fetch the latest 20 emails
                    $select: 'subject,from,receivedDateTime,bodyPreview,isRead',
                    $orderby: 'receivedDateTime DESC'
                }
            });
            setEmails(response.data.value || []);
        } catch (err) {
            console.error('Error fetching emails:', err);
            setError('Failed to fetch emails. Make sure your Azure AD App has Mail.Read permissions.');
        } finally {
            setLoading(false);
        }
    }, [acquireToken]);

    useEffect(() => {
        if (account) fetchEmails();
    }, [account, fetchEmails]);

    const handleConnect = async () => {
        if (!CLIENT_ID) return;
        setConnecting(true);
        setError('');
        try {
            await acquireToken(true);
        } catch (err) {
            console.error('Outlook connect failed', err);
            setError('Could not connect to Outlook. Please try again.');
        } finally {
            setConnecting(false);
        }
    };

    const handleLogout = () => {
        if (ipmsUser) {
            localStorage.removeItem(`outlook_account_${ipmsUser.id}`);
        }
        setAccount(null);
        setEmails([]);
    };

    return (
        <Page title="Outlook Email">
            <Container maxWidth="xl">
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={4}>
                    <Typography variant="h4" gutterBottom>
                        Outlook Inbox
                    </Typography>
                    {account && (
                        <Button variant="outlined" color="error" onClick={handleLogout} startIcon={<Iconify icon="eva:log-out-outline" />}>
                            Disconnect
                        </Button>
                    )}
                </Stack>

                {userLoading ? (
                    <Paper sx={{ p: 5, textAlign: 'center', bgcolor: '#f4f6f8' }}>
                        <CircularProgress />
                    </Paper>
                ) : !CLIENT_ID ? (
                    <Paper sx={{ p: 5, textAlign: 'center', bgcolor: '#f4f6f8' }}>
                        <Iconify icon="logos:microsoft-icon" width={60} height={60} sx={{ mb: 2 }} />
                        <Typography variant="h5" gutterBottom>
                            Outlook Integration Not Yet Configured
                        </Typography>
                        <Alert severity="info" sx={{ maxWidth: 700, mx: 'auto', mt: 2, textAlign: 'left' }}>
                            <strong>Developer Note:</strong> Set <code>REACT_APP_OUTLOOK_CLIENT_ID</code> (and, if this
                            organization uses a single-tenant app registration, <code>REACT_APP_OUTLOOK_TENANT_ID</code>) in
                            the client's <code>.env</code> file. The Azure AD app registration must be a{' '}
                            <strong>Single-page application (SPA)</strong> platform with redirect URI{' '}
                            <code>{REDIRECT_URI}</code> and the delegated Microsoft Graph permissions{' '}
                            <code>User.Read</code> and <code>Mail.Read</code>.
                        </Alert>
                    </Paper>
                ) : !account ? (
                    <Paper sx={{ p: 5, textAlign: 'center', bgcolor: '#f4f6f8' }}>
                        <Iconify icon="logos:microsoft-icon" width={60} height={60} sx={{ mb: 2 }} />
                        <Typography variant="h5" gutterBottom>
                            Connect your Outlook Account
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 4 }}>
                            Sign in with your own Microsoft account to view your own recent emails inside IPMS. No one
                            else's mailbox is ever shown here — each person connects their own.
                        </Typography>
                        {error && <Alert severity="error" sx={{ maxWidth: 600, mx: 'auto', mb: 3 }}>{error}</Alert>}
                        <Button variant="contained" size="large" onClick={handleConnect} disabled={connecting} startIcon={connecting ? <CircularProgress size={18} color="inherit" /> : <Iconify icon="eva:email-fill" />}>
                            {connecting ? 'Connecting…' : 'Connect to Outlook'}
                        </Button>
                    </Paper>
                ) : (
                    <Paper sx={{ p: 0, overflow: 'hidden' }}>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                                <CircularProgress />
                            </Box>
                        ) : error ? (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Typography color="error" gutterBottom>{error}</Typography>
                                <Button variant="outlined" onClick={fetchEmails} sx={{ mt: 2 }}>Try Again</Button>
                            </Box>
                        ) : emails.length === 0 ? (
                            <Box sx={{ p: 5, textAlign: 'center' }}>
                                <Typography color="textSecondary">Your inbox is empty.</Typography>
                            </Box>
                        ) : (
                            <List disablePadding>
                                {emails.map((email, index) => (
                                    <React.Fragment key={email.id}>
                                        <ListItem alignItems="flex-start" sx={{ p: 3, bgcolor: email.isRead ? 'transparent' : '#f0f7ff', '&:hover': { bgcolor: '#fafafa' } }}>
                                            <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>{email.from?.emailAddress?.name?.charAt(0) || 'M'}</Avatar>
                                            <ListItemText
                                                primary={<Typography variant="subtitle1" sx={{ fontWeight: email.isRead ? 'normal' : 'bold' }}>{email.from?.emailAddress?.name || email.from?.emailAddress?.address}</Typography>}
                                                secondary={<><Typography variant="body2" sx={{ fontWeight: email.isRead ? 'normal' : 'bold', color: 'text.primary', display: 'block' }}>{email.subject || '(No Subject)'}</Typography><Typography variant="body2" color="textSecondary" noWrap>{email.bodyPreview}</Typography></>}
                                            />
                                            <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap', ml: 2 }}>{new Date(email.receivedDateTime).toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</Typography>
                                        </ListItem>
                                        {index < emails.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </List>
                        )}
                    </Paper>
                )}
            </Container>
        </Page>
    );
}
